// Action buttons for a hosted site (copy link, open, preview, delete) — always visible so touch and keyboard users reach them

import { Check, Copy, ExternalLink, Eye, Trash2 } from 'lucide-react'
import type { HostedSite } from '../../hooks/use-hosted-sites'
import { useT } from '../../i18n'
import { cn } from '../../lib/utils'
import { iconButtonClass } from '../ui/styles'

interface SiteActionsProps {
  site: HostedSite
  publicUrl: string
  copied: boolean
  onCopyLink: (site: HostedSite) => void
  onPreview: (site: HostedSite) => void
  onDelete: (site: HostedSite) => void
  className?: string
}

export function SiteActions({ site, publicUrl, copied, onCopyLink, onPreview, onDelete, className }: SiteActionsProps) {
  const t = useT()
  const name = site.filename
  return (
    <div className={cn('flex shrink-0 items-center gap-0.5', className)}>
      <button
        type="button"
        onClick={() => onCopyLink(site)}
        aria-label={t('sites.action.copyNamed', { name })}
        title={t('sites.action.copy')}
        className={iconButtonClass}
      >
        {copied ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
      </button>
      <a
        href={publicUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t('sites.action.openNamed', { name })}
        title={t('sites.action.open')}
        className={iconButtonClass}
      >
        <ExternalLink className="h-4 w-4" />
      </a>
      <button
        type="button"
        onClick={() => onPreview(site)}
        aria-label={t('sites.action.previewNamed', { name })}
        title={t('sites.action.preview')}
        className={iconButtonClass}
      >
        <Eye className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={() => onDelete(site)}
        aria-label={t('sites.action.deleteNamed', { name })}
        title={t('sites.action.delete')}
        className={cn(iconButtonClass, 'text-danger hover:bg-danger-soft hover:text-danger')}
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  )
}
